import React, { FC } from 'react';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';
import { TypeAccordionSectionFields } from '@/core/generated-types';
import { TW_VERTICAL_PADDINGS } from '@/core/constants';
import classNames from 'classnames';
import { RichText } from '../RichText';
import { Document } from '@contentful/rich-text-types';
import { DynamicComponent } from '../DynamicComponent';

const AccordionSection: FC<TypeAccordionSectionFields> = ({
  title,
  titleAsH1,
  subTitle,
  items,
  containerVerticalPadding = 0,
  containerHorizontalPadding = 0,
  // Note: This default was recommended by Teri for all AccordionSections. This can
  // be overridden by adding an explicit maxWidth to an AccordionSection in Contentful
  maxWidth = '800px',
  componentAlignment = 'center',
  anchorId,
  richText,
  backgroundColor,
}) => {
  return (
    <>
      {anchorId && (
        <div
          id={anchorId}
          className="relative -top-[110px]"
          data-testid="accordion-section-anchor"
        />
      )}
      <div className={`bg-${backgroundColor?.fields?.color}`}>
        <div
          className={classNames(
            TW_VERTICAL_PADDINGS[containerVerticalPadding],
            'container',
            'mx-auto',
            `px-6 lg:px-${containerHorizontalPadding}`,
          )}
          style={{ maxWidth }}
          data-testid="accordion-section-container"
        >
          <div
            className={classNames(
              'flex flex-col',
              componentAlignment === 'left' &&
                'lg:flex-row lg:gap-8 lg:justify-between',
            )}
          >
            <div className="flex-1">
              <Heading
                tag={titleAsH1 ? HeadingTag.H1 : HeadingTag.H2}
                size={titleAsH1 ? HeadingSize.H1 : HeadingSize.H2}
                text={title}
                align={componentAlignment}
                classes={
                  componentAlignment === 'center' ? 'pb-8 md:pb-12' : 'py-10'
                }
              />
              <Heading
                tag={HeadingTag.H2}
                size={HeadingSize.H2}
                text={subTitle}
                align="left"
                classes="pb-6 text-indigo"
              />
            </div>
            <div className="flex flex-col flex-1">
              {items &&
                items.map((item) => {
                  item.sys.contentType.sys.id = 'accordion';
                  return (
                    <DynamicComponent component={item} key={item.sys.id} />
                  );
                })}
            </div>
          </div>
          {richText && (
            <div className="border-t pt-8 text-center text-sm -mb-8">
              <RichText
                openLinkSameWindow={true}
                content={richText as Document}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(AccordionSection);
